'use client';

import { useState } from 'react';

import { useCurrentUserId, useLogin } from '@ps/ui/contexts/auth';
import { useCurrentUserProfile } from '@ps/ui/contexts/user-profile';
import { Send } from 'lucide-react';

import { CommentAvatar } from './CommentAvatar';

interface CommentInputBarProps {
  /**
   * Handles the trimmed comment text — invoked only when a
   * signed-in user submits non-empty text. The host decides
   * what happens (optimistic list insert, count bump, …).
   * Throwing keeps the text in the box and surfaces an inline
   * error, so a host doing an optimistic insert can revert and
   * rethrow.
   */
  onSubmit: (text: string) => Promise<void>;
  /**
   * Whether a create is in flight — owned by the host (e.g.
   * `useOptimisticComments`'s transition `isSubmitting`) so the
   * pending state lives in one place. Disables the field and
   * guards a duplicate submit; defaults to `false`.
   */
  submitting?: boolean;
  /**
   * Merged onto the root so each host owns its own container
   * chrome (padding, border, safe-area) — the panel side-rail
   * and the mobile pinned bar style it differently.
   */
  className?: string;
}

/**
 * The single comment compose input. Owns the auth gate
 * (anonymous submit redirects to login) plus text + inline error
 * state; delegates the create to the host via `onSubmit` and the
 * pending flag to `submitting`. Driven by `useOptimisticComments`
 * from `CommentsPanel`.
 */
export function CommentInputBar({
  onSubmit,
  submitting = false,
  className,
}: CommentInputBarProps) {
  const login = useLogin();
  const currentUserId = useCurrentUserId();
  const currentProfile = useCurrentUserProfile();
  const [text, setText] = useState('');
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    const trimmed = text.trim();
    if (!trimmed) return;
    // Auth loads lazily — `currentUserId` is `undefined` until it resolves.
    // Ignore a submit made before then, else a signed-in viewer would be
    // hard-redirected to /login. Only a resolved `null` (anonymous) logs in.
    if (currentUserId === undefined) return;
    if (currentUserId === null) {
      login();
      return;
    }
    if (submitting) return;
    setError(null);
    setText('');
    try {
      await onSubmit(trimmed);
    } catch (err) {
      setText(trimmed);
      setError("Couldn't post comment. Try again.");
      console.error('[CommentInputBar] submit failed', err);
    }
  };

  return (
    <div className={className}>
      {error ? <p className="mb-2 text-[12px] text-red-600">{error}</p> : null}
      <form
        onSubmit={e => {
          e.preventDefault();
          void handleSubmit();
        }}
        className="flex items-center gap-2"
      >
        <CommentAvatar
          src={currentProfile?.avatar}
          fallback={
            currentProfile?.storeFrontName ?? currentProfile?.urlCode ?? '?'
          }
        />
        <div className="relative flex-1">
          <input
            type="text"
            value={text}
            onChange={e => setText(e.target.value)}
            placeholder="Enter comment…"
            disabled={submitting}
            className="h-10 w-full rounded-full border border-black/10 bg-black/[0.03] pl-4 pr-10 text-[13px] outline-none focus:border-black/30 disabled:opacity-50"
          />
          <button
            type="submit"
            disabled={submitting || !text.trim()}
            aria-label="Post comment"
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-black/50 transition hover:text-black disabled:opacity-30"
          >
            <Send className="h-4 w-4" aria-hidden />
          </button>
        </div>
      </form>
    </div>
  );
}
